import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCardsByOrganization,
  submitCardQuiz,
} from "../store/reducers/quizzesSlice";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaSearchPlus,
  FaSearchMinus,
} from "react-icons/fa";
import { Box, Typography, Paper } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import "bootstrap/dist/css/bootstrap.min.css";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import QuizAlertModal from "./components/QuizAlertModal";
import { setQuizAlertModal } from "../store/reducers/modalSlice";
import { toWords } from "number-to-words";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const CardPdfViewer = () => {
  const dispatch = useDispatch();

  const { quizAlertModal } = useSelector((state) => state.modal || {});
  const { cardQuiz, cards } = useSelector((state) => state.quizzes || {});
  const { organizationDetails } = useSelector((state) => state.organization);

  const [selectedCardForQuiz, setSelectedCardForQuiz] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewType, setViewType] = useState("front");
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(1.2);
  const [fileBlob, setFileBlob] = useState(null);

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(fetchCardsByOrganization(organizationDetails.id));
    }
  }, [organizationDetails?.id, dispatch]);

  const selectedCard = useMemo(
    () => cards?.data?.[selectedIndex],
    [cards, selectedIndex]
  );

  const rawUrl =
    viewType === "front" ? selectedCard?.front_pdf : selectedCard?.back_pdf;

  useEffect(() => {
    if (!rawUrl) {
      setFileBlob(null);
      return;
    }

    let objectUrl;

    const loadPdf = async () => {
      try {
        const res = await fetch(rawUrl, { cache: "no-store" });
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setFileBlob(objectUrl);
      } catch (err) {
        console.error("PDF load error", err);
      }
    };

    loadPdf();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rawUrl]);

  const handleNextCard = () => {
    if (selectedIndex < (cards?.data?.length || 0) - 1) {
      setSelectedIndex((prev) => prev + 1);
      setViewType("front");
      setZoom(1.2);
    }
  };

  const handlePrevCard = () => {
    if (selectedIndex > 0) {
      setSelectedIndex((prev) => prev - 1);
      setViewType("front");
      setZoom(1.2);
    }
  };

  const handleDone = () => {
    if (viewType !== "back") {
      alert("Please view back side first");
      return;
    }

    setSelectedCardForQuiz(selectedCard);
    dispatch(setQuizAlertModal(true));
  };

  const handleYes = async () => {
    if (!selectedCardForQuiz) return;

    dispatch(setQuizAlertModal(false));

    const payload = {
      card_id: selectedCardForQuiz?.id,
      organization_id: organizationDetails?.id,
      want_card_quiz: true,
    };

    await dispatch(submitCardQuiz(payload));

    setSelectedCardForQuiz(null);
  };

  const handleNo = () => {
    setSelectedCardForQuiz(null);
    dispatch(setQuizAlertModal(false));
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.6));

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const number_of_quiz = toWords(
    organizationDetails?.cards_questions_per_quiz || 0
  );

  return (
    <Box sx={{ display: "flex", height: "93vh" }}>
      <Box
        sx={{
          width: "28%",
          overflowY: "auto",
          borderRight: "1px solid #ddd",
          p: 2,
        }}
      >
        {cards?.data?.map((card, index) => (
          <Paper
            key={card.id}
            onClick={() => {
              setSelectedIndex(index);
              setViewType("front");
              setZoom(1.2);
            }}
            sx={{
              p: 1.5,
              mb: 1,
              cursor: "pointer",
              backgroundColor: index === selectedIndex ? "#ffebee" : "#fff",
              border:
                index === selectedIndex
                  ? "1px solid #ef5350"
                  : "1px solid #eee",
            }}
          >
            <Typography variant="body2">
              <strong>{card.ac_no}</strong> - {card.english_name}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          width: "72%",
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: "6px",
            backgroundColor: "#f5f5f5",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedCard?.english_name} -{" "}
            {viewType === "front" ? "Front" : "Back"}
          </Typography>

          <Box>
            <button className="btn btn-light btn-sm me-1" onClick={zoomOut}>
              <FaSearchMinus />
            </button>
            <button className="btn btn-light btn-sm" onClick={zoomIn}>
              <FaSearchPlus />
            </button>
          </Box>
        </Box>

        <Box className="mb-2 text-center">
          <button
            className={`btn btn-sm me-2 ${
              viewType === "front" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setViewType("front")}
          >
            Front
          </button>

          <button
            className={`btn btn-sm ${
              viewType === "back" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setViewType("back")}
          >
            Back
          </button>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: "8px",
            p: 2,
            backgroundColor: "#fafafa",
          }}
        >
          {fileBlob ? (
            <Document
              key={fileBlob}
              file={fileBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <span className="spinner-border spinner-border-sm"></span>
              }
            >
              {Array.from(new Array(numPages), (_, i) => (
                <Box key={i} mb={2} display="flex" justifyContent="center">
                  <Page
                    pageNumber={i + 1}
                    scale={zoom}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              ))}
            </Document>
          ) : (
            <Typography align="center" mt={5}>
              No PDF available
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 1, display: "flex", justifyContent: "center", gap: 1 }}>
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
            onClick={handlePrevCard}
            disabled={selectedIndex === 0}
          >
            <FaArrowLeft size={14} />
            Prev Card
          </button>

          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={handleNextCard}
            disabled={selectedIndex === (cards?.data?.length || 0) - 1}
          >
            Next Card
            <FaArrowRight size={14} />
          </button>

          {viewType === "back" && (
            <button
              className="btn btn-success btn-sm d-flex align-items-center gap-1"
              onClick={handleDone}
            >
              <FaCheck size={14} />
              Done
            </button>
          )}
        </Box>
      </Box>

      {quizAlertModal && (
        <QuizAlertModal
          description={`You have read an Action card. Do you want to increase your score by answering ${number_of_quiz} questions?`}
          subText={`Questions will be sent after ${organizationDetails?.delay_cards_quiz} hours.`}
          open={quizAlertModal}
          loading={cardQuiz?.loading}
          onClose={handleNo}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default CardPdfViewer;
